/**
 * Error Screen Component
 *
 * Displays payment failure errors for CHAT timeline.
 * Three error types: retry-later, dont-retry, max-attempts.
 */

import { type FC } from 'react';
import type { ChatErrorScreenConfig } from '@/types/chat-timeline.types';
import {
  getErrorMessage,
  getErrorTitle,
  getButtonLabel,
} from '@utils/errorMapper';
import styles from './ErrorScreen.module.css';

/**
 * ErrorScreen Props
 */
export interface ErrorScreenProps {
  /**
   * Error configuration (type, message, code, attempt number)
   */
  errorConfig: ChatErrorScreenConfig;

  /**
   * Callback when "Go back" button clicked
   */
  onGoBack: () => void;

  /**
   * Callback for session-expired and sse-disconnected action buttons.
   * session-expired: emits payment.completed postMessage (or redirects).
   * sse-disconnected: reloads the page.
   */
  onAction?: () => void;
}

/**
 * Warning icon SVG (blue triangle with exclamation from design)
 */
const WarningIcon: FC = () => (
  <svg
    width="80"
    height="73"
    viewBox="0 0 80 73"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      opacity="0.1"
      fillRule="evenodd"
      clipRule="evenodd"
      d="M32.6511 9.71326C36.8698 2.4585 47.4237 2.45849 51.6424 9.71326L78.5168 55.9277C82.7332 63.1784 77.4586 72.2388 69.0211 72.2388H15.2724C6.83496 72.2388 1.56036 63.1784 5.77676 55.9277L32.6511 9.71326Z"
      fill="#06A4F3"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M28.4108 5.63491C32.7799 -1.8783 43.7087 -1.87831 48.0777 5.63491L74.952 51.8493C79.3201 59.3608 73.8545 68.7434 65.1186 68.7434H11.37C2.63403 68.7434 -2.8315 59.3608 1.53654 51.8493L28.4108 5.63491ZM47.4022 6.024C43.3338 -0.972317 33.1548 -0.972315 29.0863 6.024L2.21204 52.2384C-1.85273 59.2284 3.23094 67.9666 11.37 67.9666H65.1186C73.2576 67.9666 78.3413 59.2284 74.2765 52.2384L47.4022 6.024ZM35.1141 27.5694C35.1141 25.852 36.5171 24.462 38.2451 24.462C39.973 24.462 41.376 25.852 41.376 27.5694V41.1645C41.376 42.8818 39.973 44.2719 38.2451 44.2719C36.5171 44.2719 35.1141 42.8818 35.1141 41.1645V27.5694ZM35.1141 52.0406C35.1141 50.3233 36.5171 48.9332 38.2451 48.9332C39.973 48.9332 41.376 50.3233 41.376 52.0406C41.376 53.7579 39.973 55.148 38.2451 55.148C36.5171 55.148 35.1141 53.7579 35.1141 52.0406Z"
      fill="#06A4F3"
    />
  </svg>
);

/**
 * ErrorScreen Component
 *
 * Renders simplified error screen with:
 * - Warning icon (blue triangle)
 * - Title
 * - Message text
 * - "Go back" button (hidden for max-attempts)
 */
export const ErrorScreen: FC<ErrorScreenProps> = ({
  errorConfig,
  onGoBack,
  onAction,
}) => {
  const { errorType, errorCode, errorMessage } = errorConfig;

  const title = getErrorTitle(errorType);
  const message = getErrorMessage(errorType, errorCode, errorMessage);
  const buttonLabel = getButtonLabel(errorType);
  const handleAction =
    errorType === 'session-expired' || errorType === 'sse-disconnected'
      ? onAction
      : onGoBack;

  return (
    <div className={styles.container}>
      <div className={styles.iconWrapper}>
        <WarningIcon />
      </div>

      <h1 className={styles.title}>{title}</h1>

      <p className={styles.message}>{message}</p>

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.goBackButton}
          onClick={handleAction}
          aria-label={buttonLabel}
        >
          {buttonLabel}
        </button>
      </div>
    </div>
  );
};
